import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { StudentWelcomeModal } from "@/components/StudentWelcomeModal";
import { setDateFormatPref } from "@/lib/format";
import {
  SquaresFour, Student, Receipt, CurrencyDollar, ListChecks, Bank, Gear, SignOut, List, X, Gift, FileText, UploadSimple, HandCoins, CaretDown, Wallet, ChatCircleText, CheckCircle as CheckCircleIcon, BookOpen, Question, LinkSimple, PushPin, ArrowLineRight, ArrowLineLeft, ChartLineUp, Sparkle,
} from "@phosphor-icons/react";
import { MoneyEnvelope } from "@/components/icons";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { VersionWatcher } from "@/components/VersionWatcher";
import { useAskAI } from "@/context/AskAIContext";
import { useModalOpen } from "@/lib/useModalOpen";
import { Lockup } from "@/components/Logo";

const OWNER_NAV_SECTIONS = [
  { section: "529 COMPLIANCE", items: [
    { to: "/", label: "Overview", icon: SquaresFour, end: true },
    { to: "/students", label: "Beneficiary(ies)", icon: Student },
    { to: "/scholarships", label: "Scholarships & Aid", icon: BookOpen },
    { to: "/expenses", label: "Expenses", icon: CurrencyDollar, attentionKey: "unclassified_expenses" },
    // TWO KEYS, BECAUSE THIS PAGE NOW HOLDS TWO KINDS OF WAITING. Receipts
    // nobody has opened, and receipts somebody opened and set aside to decide
    // later (ZEN-634) — both sit on this page and both are work. Counting only
    // the first would leave a family with nothing on the menu after they parked
    // the last unopened receipt, which is the burying this feature exists to
    // stop. The marker is the sum; the Overview is where the two are told apart.
    { to: "/import", label: "Imports & Receipts", icon: UploadSimple, attentionKey: ["receipt_queue", "set_aside_receipts"] },
    { to: "/approvals", label: "Approvals", icon: CheckCircleIcon, attentionKey: "approvals" },
    { to: "/withdrawals", label: "529 Distributions", icon: Bank },
    { to: "/ledger", label: "Money Trace", icon: ListChecks },
    { to: "/projections", label: "529 Projections", icon: ChartLineUp },
    { to: "/tax-forms", label: "Tax Forms & Audit", icon: FileText },
  ]},
  { section: "FAMILY MONEY TOOLS", subLabel: "· not tax records", items: [
    { label: "Money Envelopes", icon: MoneyEnvelope, children: [
      { to: "/contributions", label: "Contributions", icon: HandCoins },
      { to: "/advances", label: "Cash Advances", icon: HandCoins },
      { to: "/gifts", label: "Gifts", icon: Gift },
    ]},
  ]},
  { section: "SETUP & HELP", items: [
    { to: "/accounts", label: "Accounts & Payers", icon: Wallet },
    { to: "/settings", label: "Settings", icon: Gear },
    { to: "/help", label: "Help Center", icon: Question },
    { action: "ask-ai", label: "Ask AI", icon: Sparkle },
    { to: "/resources", label: "Resources", icon: LinkSimple },
  ]},
];
const STUDENT_NAV = [
  { to: "/", label: "Overview", icon: SquaresFour, end: true },
  { to: "/expenses", label: "My Expenses", icon: CurrencyDollar },
  { to: "/my-receipts", label: "My Receipts", icon: Receipt },
  { to: "/ledger", label: "Money Trace", icon: ListChecks },
  { to: "/help", label: "Help Center", icon: Question },
  { action: "ask-ai", label: "Ask AI", icon: Sparkle },
  { to: "/resources", label: "Resources", icon: LinkSimple },
];

export function NavItemLink({ n, onNavigate, nested, attentionByKey, collapsed, onAction }) {
  // ONE KEY OR SEVERAL, WITHOUT THE CALLER HAVING TO CARE. This read a single
  // key, so a menu item covering more than one kind of waiting work could only
  // ever count one of them — and adding a second kind silently under-counted
  // rather than failing. The identifier on the marker stays the first key, so
  // nothing that looks for `badge-receipt_queue` moves.
  const attentionKeys = n.attentionKey ? [].concat(n.attentionKey) : [];
  const badgeCount = attentionKeys.reduce((total, k) => total + ((attentionByKey || {})[k] || 0), 0);
  const testId = `nav-${n.label.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-")}`;

  // Action-based items (e.g. Ask AI drawer trigger) render a button, not a NavLink
  if (n.action) {
    const handleClick = () => { if (onAction) onAction(n.action); if (onNavigate) onNavigate(); };
    if (collapsed) {
      return (
        <button type="button" onClick={handleClick} data-testid={testId} title={n.label}
          className="group relative flex h-10 w-10 items-center justify-center rounded-md text-[#1f2937] transition-colors hover:bg-[#E9E9EB]">
          <n.icon size={20} weight="bold" aria-hidden="true" />
          <span className="pointer-events-none absolute left-full ml-2 hidden whitespace-nowrap rounded-md bg-[#1f2937] px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block z-50">{n.label}</span>
        </button>
      );
    }
    return (
      <button type="button" onClick={handleClick} data-testid={testId}
        className={`flex w-full items-center gap-3 rounded-md py-1.5 text-sm font-semibold text-[#1f2937] transition-colors hover:bg-[#E9E9EB] ${nested ? "pl-9 pr-3" : "px-3"}`}>
        <n.icon size={nested ? 17 : 20} weight="bold" aria-hidden="true" />
        {n.label}
      </button>
    );
  }

  if (collapsed) {
    return (
      <NavLink
        to={n.to}
        end={n.end}
        data-testid={`nav-${n.label.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-")}`}
        onClick={onNavigate}
        title={n.label}
        className={({ isActive }) =>
          `group relative flex h-10 w-10 items-center justify-center rounded-md transition-colors ${
            isActive ? "bg-[#034AA2] text-white" : "text-[#1f2937] hover:bg-[#E9E9EB]"
          }`
        }
      >
        <n.icon size={20} weight="bold" aria-hidden="true" />
        {badgeCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#D97706] px-1 text-[8px] font-bold text-white" aria-label={`${badgeCount} items awaiting review`} data-testid={`badge-${attentionKeys[0]}`}>
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
        <span className="pointer-events-none absolute left-full ml-2 hidden whitespace-nowrap rounded-md bg-[#1f2937] px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block z-50">
          {n.label}
        </span>
      </NavLink>
    );
  }
  return (
    <NavLink
      to={n.to}
      end={n.end}
      data-testid={`nav-${n.label.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-")}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md py-1.5 text-sm font-semibold transition-colors ${nested ? "pl-9 pr-3" : "px-3"} ${
          isActive ? "bg-[#034AA2] text-white" : "text-[#1f2937] hover:bg-[#E9E9EB]"
        }`
      }
    >
      <n.icon size={nested ? 17 : 20} weight="bold" aria-hidden="true" />
      {n.label}
      {badgeCount > 0 && (
        <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#D97706] px-1.5 text-[10px] font-bold text-white" aria-label={`${badgeCount} items awaiting review`} data-testid={`badge-${attentionKeys[0]}`}>
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}
    </NavLink>
  );
}

function NavGroup({ item, onNavigate, collapsed }) {
  const location = useLocation();
  const childActive = item.children.some((c) => location.pathname === c.to);
  const [open, setOpen] = useState(childActive);
  const [flyout, setFlyout] = useState(false);
  React.useEffect(() => { if (childActive) setOpen(true); }, [childActive]);

  if (collapsed) {
    return (
      <div className="relative" onMouseEnter={() => setFlyout(true)} onMouseLeave={() => setFlyout(false)}>
        <button
          type="button"
          title={item.label}
          data-testid={`nav-group-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={`group relative flex h-10 w-10 items-center justify-center rounded-md transition-colors ${
            childActive ? "text-[#034AA2] bg-[#034AA2]/10" : "text-[#1f2937] hover:bg-[#E9E9EB]"
          }`}
        >
          <item.icon size={20} weight="bold" aria-hidden="true" />
        </button>
        {flyout && (
          <div className="absolute left-full top-0 z-50 ml-1.5 min-w-[180px] rounded-lg border border-[#C2C9D1] bg-white py-1 shadow-xl">
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">{item.label}</div>
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={onNavigate}
                data-testid={`nav-${c.label.toLowerCase().replace(/[()]/g, "").replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "bg-[#034AA2] text-white" : "text-[#1f2937] hover:bg-[#E9E9EB]"
                  }`
                }
              >
                <c.icon size={17} weight="bold" />
                {c.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`flex w-full items-center gap-3 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
          childActive ? "text-[#034AA2]" : "text-[#1f2937] hover:bg-[#E9E9EB]"
        }`}
      >
        <item.icon size={20} weight="bold" aria-hidden="true" />
        {item.label}
        <CaretDown size={14} weight="bold" className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 flex flex-col gap-0.5">
          {item.children.map((c) => <NavItemLink key={c.to} n={c} onNavigate={onNavigate} nested />)}
        </div>
      )}
    </div>
  );
}

export function StudentSelector() {
  const { user, selectedStudentId, selectStudent } = useAuth();
  const { data: students, isSuccess } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data, enabled: user?.role === "owner" });
  // Prune a stale selected student id (e.g. a deleted student left in localStorage)
  // so the dashboard never requests a ghost student and 404s.
  React.useEffect(() => {
    if (!isSuccess || !selectedStudentId) return;
    if (!(students || []).some((s) => s.id === selectedStudentId)) selectStudent(null);
  }, [isSuccess, students, selectedStudentId, selectStudent]);
  if (user?.role !== "owner") return null;
  const list = students || [];
  const active = list.filter((s) => !s.archived);
  const archived = list.filter((s) => s.archived);
  return (
    <select
      data-testid="student-selector"
      value={selectedStudentId || ""}
      onChange={(e) => selectStudent(e.target.value || null)}
      className="h-9 max-w-[180px] truncate rounded-md border border-[#C2C9D1] bg-white px-2 text-sm font-medium text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#034AA2] sm:max-w-none sm:px-3"
    >
      <option value="">All beneficiaries</option>
      {archived.length > 0 ? (
        <>
          <optgroup label="Active" data-testid="student-selector-active-group">
            {active.map((s) => (
              <option key={s.id} value={s.id}>{s.full_name}</option>
            ))}
          </optgroup>
          <optgroup label="Archived" data-testid="student-selector-archived-group">
            {archived.map((s) => (
              <option key={s.id} value={s.id}>{s.full_name}</option>
            ))}
          </optgroup>
        </>
      ) : (
        list.map((s) => (
          <option key={s.id} value={s.id}>{s.full_name}</option>
        ))
      )}
    </select>
  );
}

export default function Layout({ children }) {
  const { user, logout, selectedStudentId } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isWide, setIsWide] = useState(typeof window !== "undefined" ? window.innerWidth >= 1440 : true);
  const [userCollapsed, setUserCollapsed] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("sidebar_collapsed") : null;
    return stored !== null ? stored === "true" : null;
  });
  const isOwner = user?.role !== "student";

  // Track viewport width for sidebar collapse threshold
  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 1440);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // User choice overrides width-based default; null = no explicit choice
  const sidebarExpanded = userCollapsed !== null ? !userCollapsed : isWide;

  const toggleSidebar = () => {
    const collapse = sidebarExpanded;
    setUserCollapsed(collapse);
    localStorage.setItem("sidebar_collapsed", String(collapse));
  };

  // Sync date format preference from backend settings on load (persists across devices)
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data, enabled: !!user });
  React.useEffect(() => { if (settings?.date_format) setDateFormatPref(settings.date_format); }, [settings?.date_format]);

  // ★ THE NUMBER BESIDE A MENU ITEM COUNTS THE BENEFICIARY IN THE DROPDOWN,
  // AND THIS LINE IS THE BUG SHE REPORTED. (ZEN-687, seen in production
  // 2026-09-22.) The menu asked for the whole household and every page it
  // links to shows one beneficiary, so the menu said four approvals were
  // waiting and the page said there were none. The server has taken a
  // beneficiary since the day it was written; the menu simply never sent one.
  //
  // THE SELECTION IS PART OF WHAT IDENTIFIES THIS ANSWER, not just part of the
  // request. Without it in the key, switching beneficiary would go on showing
  // the previous one's numbers from memory until they went stale — the same
  // disagreement between the menu and the page, lasting half a minute instead
  // of for ever, which is harder to notice rather than better.
  const { data: attention } = useQuery({
    queryKey: ["attention", selectedStudentId || "all"],
    queryFn: async () => (await api.get(
      `/attention${selectedStudentId ? `?student_id=${encodeURIComponent(selectedStudentId)}` : ""}`)).data,
    enabled: user?.role === "owner",
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
  const attentionByKey = {};
  for (const s of (attention?.sources || [])) {
    attentionByKey[s.key] = s.count;
  }

  // Approvals nav visibility — show only when a beneficiary has a login OR approvals exist
  const { data: students } = useQuery({
    queryKey: ["students"],
    queryFn: async () => (await api.get("/students")).data,
    enabled: user?.role === "owner",
    staleTime: 60000,
  });
  const showApprovals = React.useMemo(() => {
    if (!isOwner) return false;
    const hasLogin = (students || []).some(s => !!s.login_email);
    const hasApprovals = (attentionByKey.approvals || 0) > 0;
    return hasLogin || hasApprovals;
  }, [isOwner, students, attentionByKey.approvals]);

  const { openDrawer: openAI, toggleDrawer: toggleAI, open: aiOpen } = useAskAI();
  const modalOpen = useModalOpen();
  const handleNavAction = (action) => { if (action === "ask-ai") toggleAI(); };

  const renderNavItems = (items, onNav, extraProps) => items.map((n) => n.children
    ? <NavGroup key={n.label} item={n} onNavigate={onNav} {...extraProps} />
    : <NavItemLink key={n.to || n.action} n={n} onNavigate={onNav} attentionByKey={attentionByKey} onAction={handleNavAction} {...extraProps} />
  );

  const renderNavFull = () => (
    <nav className="flex flex-col gap-0.5">
      {isOwner ? OWNER_NAV_SECTIONS.map((sec, si) => {
        const items = sec.items.filter(n => n.to !== "/approvals" || showApprovals);
        return (
        <div key={sec.section} className={si > 0 ? "mt-1.5 border-t border-[#E5E7EB] pt-1.5" : ""}>
          <div className="mb-0.5 px-3 flex items-baseline gap-1.5" data-testid={`nav-section-${sec.section.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#034AA2]">{sec.section}</span>
            {sec.subLabel && <span className="text-[10px] font-medium text-[#6b7280]">{sec.subLabel}</span>}
          </div>
          {renderNavItems(items, () => setMobileOpen(false))}
        </div>
        );
      }) : renderNavItems(STUDENT_NAV, () => setMobileOpen(false))}
    </nav>
  );

  const renderNavCollapsed = () => (
    <nav className="flex flex-col items-center gap-1">
      {isOwner ? OWNER_NAV_SECTIONS.map((sec, si) => {
        const items = sec.items.filter(n => n.to !== "/approvals" || showApprovals);
        return (
        <React.Fragment key={sec.section}>
          {si > 0 && <div className="my-1.5 h-px w-8 bg-[#E9E9EB]" />}
          {renderNavItems(items, () => {}, { collapsed: true })}
        </React.Fragment>
        );
      }) : renderNavItems(STUDENT_NAV, () => {}, { collapsed: true })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#E9E9EB]">
      {/* Top bar */}
      <header className="sticky top-0 z-50 flex h-16 items-center justify-between border-b border-[#C2C9D1] bg-white px-4 lg:px-0 overflow-hidden">
        <div className="flex min-w-0 items-center gap-3 lg:gap-0">
          <button className="shrink-0 lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <List size={26} weight="bold" />
          </button>
          <div className={`flex min-w-0 items-center transition-[width] duration-200 ${sidebarExpanded ? "lg:w-60" : "lg:w-16"} lg:justify-center lg:shrink-0`}>
            <Link to="/" className="flex items-center"><Lockup height={44} /></Link>
          </div>
        </div>
        <div className={`flex shrink-0 items-center gap-2 sm:gap-3 ${aiOpen ? "lg:pr-[416px]" : "lg:pr-6"}`}>
          {/* The two helper buttons, placed in ONE spot. (ZEN-668, ZEN-753)
              From desktop width up they sit here in the top bar. Below that
              this same box is a solid bar fixed to the foot of the screen, and
              the page ends above it. Either way they are in the frame, never
              on top of the page. Ask AI is last so it is on the right, under
              the thumb, on a phone. While the Ask AI panel is open on a
              desktop the row steps left of it so the button can close it.
              BOTH ARE HIDDEN WHILE A DIALOG IS OPEN — see lib/useModalOpen.js.
              (ZEN-169) */}
          <div
            data-testid="helper-bar"
            className="fixed inset-x-0 bottom-0 flex h-14 items-center justify-between border-t border-[#C2C9D1] bg-white px-4 lg:static lg:h-auto lg:gap-2 lg:border-0 lg:bg-transparent lg:px-0"
          >
            <FeedbackWidget />
            {!modalOpen && (
            <button
              onClick={toggleAI}
              data-testid="ask-ai-fab"
              aria-label="Ask AI"
              aria-expanded={aiOpen}
              className="ml-auto flex h-10 items-center gap-2 rounded-full bg-[#034AA2] px-4 text-sm font-bold text-white active:scale-95 lg:h-9"
            >
              <Sparkle size={20} weight="bold" />
              Ask AI
            </button>
            )}
          </div>
          <StudentSelector />
          <div className="hidden items-center gap-2 sm:flex">
            <div className="text-right leading-tight">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-[11px] font-medium capitalize text-[#6b7280]">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-[#C2C9D1] text-[#6F0A11] hover:bg-[#E9E9EB]"
            aria-label="Log out"
          >
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar — full or collapsed rail */}
        <aside
          className={`sticky top-16 hidden h-[calc(100vh-4rem)] shrink-0 flex-col border-r border-[#C2C9D1] bg-white lg:flex transition-[width] duration-200 ${
            sidebarExpanded ? "w-60 p-2" : "w-16 px-1.5 py-3"
          }`}
          data-testid="desktop-sidebar"
        >
          <div className={`pb-1 ${sidebarExpanded ? "flex justify-end" : "flex justify-center"}`}>
            <button
              onClick={toggleSidebar}
              data-testid="sidebar-collapse-toggle"
              aria-label={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
              title={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
              className="flex h-8 w-8 items-center justify-center rounded-md text-[#6b7280] transition-colors hover:bg-[#E9E9EB]"
            >
              {sidebarExpanded
                ? <ArrowLineLeft size={18} weight="bold" />
                : <ArrowLineRight size={18} weight="bold" />}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {sidebarExpanded ? renderNavFull() : renderNavCollapsed()}
          </div>
          <div className={`mt-3 border-t border-[#C2C9D1] pt-2 ${sidebarExpanded ? "" : "flex flex-col items-center gap-1"}`}>
            <NavItemLink n={{ to: "/feedback", label: "Feedback", icon: ChatCircleText }} onNavigate={() => {}} collapsed={!sidebarExpanded} />
          </div>
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-64 flex flex-col bg-white p-3 shadow-xl">
              <div className="mb-4 flex items-center justify-between px-1">
                <span className="font-extrabold">Menu</span>
                <button onClick={() => setMobileOpen(false)} aria-label="Close"><X size={22} /></button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {renderNavFull()}
              </div>
              <div className="mt-6 border-t border-[#C2C9D1] pt-4">
                <NavItemLink n={{ to: "/feedback", label: "Feedback", icon: ChatCircleText }} onNavigate={() => setMobileOpen(false)} />
              </div>
            </div>
          </div>
        )}

        {/* THE BOTTOM PADDING IS MEASURED FROM THE BAR, not chosen by eye.
            Below desktop width the helper buttons sit in a 56px bar at the
            foot of the screen, and 80px clears it (ZEN-668). From desktop width
            up they are in the top bar and nothing floats (ZEN-753). */}
        <main className={`@container min-w-0 flex-1 p-4 pb-20 lg:p-6 transition-[margin] duration-200 ${aiOpen ? "sm:mr-[400px]" : ""}`}>{children}</main>
      </div>

      <VersionWatcher />
      {user?.role === "student" && <StudentWelcomeModal />}
    </div>
  );
}
